import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { IconButton, Input } from '@/components/ui';
import { normalizeUrl, type LinkEntry } from '@/lib/links';

export interface LinkRowProps {
  /** The stored link this row represents. For a brand-new draft pass an
   *  empty url and set `isDraft`. */
  link: LinkEntry;
  /** A brand-new "Add link" draft: opens straight in the editor and has no
   *  collapsed saved-link row above it. Saved links (the default) render the
   *  collapsed row and open the editor on pencil tap. */
  isDraft?: boolean;
  /** Called with the normalized entry when the user taps Save. */
  onSave: (link: LinkEntry) => void;
  /** Called when the user taps Delete (silently removes the row). */
  onDelete: () => void;
}

/**
 * A single editable link. A saved link shows a collapsed row (title or url +
 * a pen icon); tapping the pen opens an inline editor BELOW the row while the
 * collapsed row stays visible (per Figma). A draft renders only the editor.
 * The editor is a borderless slate card with "Link Title (optional)" and
 * "URL" inputs plus Delete/Save text actions. Each row owns its own editor
 * state so multiple rows can be open at once.
 */
export function LinkRow({
  link,
  isDraft = false,
  onSave,
  onDelete,
}: LinkRowProps) {
  const [expanded, setExpanded] = useState(isDraft);
  // Editing a saved link pre-fills BOTH fields from the stored entry (a draft
  // has an empty title/url, so it opens blank).
  const [title, setTitle] = useState(link.title ?? '');
  const [url, setUrl] = useState(link.url ?? '');

  function open() {
    setTitle(link.title ?? '');
    setUrl(link.url ?? '');
    setExpanded(true);
  }

  function save() {
    const normalized = normalizeUrl(url);
    if (!normalized) return; // Save is disabled when empty; guard anyway.
    onSave({ title: title.trim() || null, url: normalized });
    setExpanded(false);
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/* Collapsed saved-link row — stays visible even while editing. Drafts
          have no saved value yet, so they skip it. */}
      {!isDraft && (
        <div className="flex min-h-10 items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
            {link.title || link.url}
          </span>
          <IconButton
            aria-label="Edit link"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={open}
          >
            <Pencil size={16} />
          </IconButton>
        </div>
      )}

      {expanded && (
        <div className="flex flex-col gap-2 rounded-[4px] bg-surface-secondary p-2">
          <Input
            label="Link Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Conference sign-up"
          />
          <Input
            label="URL"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
          />
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center rounded-sm px-2 py-1.5 text-base font-semibold leading-6 text-muted-foreground transition-colors hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!url.trim()}
              className="inline-flex items-center rounded-sm px-2 py-1.5 text-base font-semibold leading-6 text-muted-foreground transition-colors hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
